/**
 * <ApprovedTabBody>
 *
 * The completed / approved reports tab body. Extracted from
 * FieldReports.js after iter-120 pointed out that the tab was now
 * only made of two shared components (`<FieldReportCard>` +
 * `renderListRow`) so keeping it inline was a size penalty for
 * no locality benefit.
 *
 * Uses the shared sort helper (`sortReports.js`) so its search +
 * sort logic can't fork from the per-type tabs like it did before.
 */
import React from 'react';
import { format } from 'date-fns';
import { FileText } from 'lucide-react';
import { Card } from '../ui/card';
import PdfStorageNotice from './PdfStorageNotice';
import FieldReportCard from './FieldReportCard';
import { KIND_COLOR, KIND_LABEL } from './status';
import { sortReports, DEFAULT_SEARCH_FIELDS } from './sortReports';

function unionCompleted({ preCons, softDigs, locates, cseForms }) {
  return [
    ...preCons.filter((p) => p.status === 'completed')
      .map((p) => ({ ...p, type: 'precon', label: 'PreCon' })),
    ...softDigs.filter((s) => s.status === 'completed')
      .map((s) => ({ ...s, type: 'softdig', label: 'Soft Dig' })),
    ...locates.filter((l) => l.status === 'completed')
      .map((l) => ({ ...l, type: 'locate', label: 'Locate' })),
    // Feb 2026 audit — CSE joins the Approved tab now that its
    // `status` field is normalized to field-report vocabulary in
    // FieldReports.js#loadForms.
    ...cseForms.filter((c) => c.status === 'completed')
      .map((c) => ({ ...c, type: 'cse', label: 'Confined Space' })),
  ];
}

function approvedTitle(report, getReportDisplayTitle) {
  if (report.type === 'cse') return report.form_number || '(Untitled)';
  return report.job_number || report.soft_dig_number || getReportDisplayTitle(report);
}

function approvedViewRoute(report) {
  if (report.type === 'cse') return `/confined-space/${report.id}`;
  const seg = report.type === 'softdig' ? 'soft-dig' : report.type;
  return `/${seg}/${report.id}?view=true`;
}

export default function ApprovedTabBody({
  // Data sources (parent owns fetching)
  preCons, softDigs, locates, cseForms,
  // Sort/search state (shared with parent's toolbar)
  sortBy, searchQuery,
  // View toggle
  viewMode,
  // Utilities (parent-owned closures — avoids re-importing page-level helpers here)
  // getReportDisplayTitle used for the visible title fallback;
  // getReportDate used for the search-sort helper.
  getReportDisplayTitle, getReportDate,
  // Navigation + backend callbacks
  navigate,
  openFieldReportPreview,
  emailPDFFromBackend,
  resetApproval,
  // Auth + shared UI
  isAdmin,
  PhotoReadinessDot,
  renderListRow,
}) {
  const allCompleted = unionCompleted({ preCons, softDigs, locates, cseForms });
  // Feb 2026 iter121 review — sort by the SAME title the user sees on
  // the Approved-tab card (job_number / form_number), not by the
  // generic getReportTitle. Prevents "Name A→Z" from ordering by a
  // string the user can't see.
  const titleForSort = (r) => approvedTitle(r, getReportDisplayTitle);
  const completedReports = sortReports(allCompleted, sortBy, searchQuery, {
    getTitle: titleForSort,
    getDate: getReportDate,
    searchFields: DEFAULT_SEARCH_FIELDS,
  });

  return (
    <>
      <PdfStorageNotice className="mb-3" />
      {allCompleted.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">No Completed Reports</h3>
          <p className="text-slate-500">
            Reports will appear here after they are approved in the Review Queue
          </p>
        </Card>
      ) : completedReports.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No approved reports match your search</p>
        </Card>
      ) : viewMode === 'list' ? (
        <div className="space-y-2">
          {completedReports.map((report) => (
            <React.Fragment key={`${report.type}-${report.id}`}>
              {renderListRow({
                kind: report.type,
                kindLabel: KIND_LABEL[report.type] || report.label,
                kindColor: KIND_COLOR[report.type] || 'bg-slate-100 text-slate-700',
                item: report,
                titleOverride: approvedTitle(report, getReportDisplayTitle),
                onView: () => navigate(approvedViewRoute(report)),
                // Approved tab is read-only — no Edit or Delete.
                // Reset-approval still available for admins via the
                // shared row's built-in Reset button.
                onPdf: () => openFieldReportPreview(report.type, report.id),
              })}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {completedReports.map((report) => (
            <FieldReportCard
              key={`${report.type}-${report.id}`}
              kind={report.type}
              kindLabel={KIND_LABEL[report.type] || report.label}
              kindColor={KIND_COLOR[report.type] || 'bg-slate-100 text-slate-700'}
              item={report}
              title={approvedTitle(report, getReportDisplayTitle)}
              location={report.site_location || report.job_service_address || report.project_name || 'No Location'}
              meta={[
                // Fall back to reviewer_name (CSE / maintenance forms
                // set reviewer_name, not reviewed_by, on approve).
                { label: 'Reviewed by', value: report.reviewed_by || report.reviewer_name || '-' },
                {
                  label: 'Approved',
                  value: report.reviewed_at
                    ? format(new Date(report.reviewed_at), 'MMM d, yyyy h:mm a')
                    : '-',
                },
              ]}
              onView={() => navigate(approvedViewRoute(report))}
              // Approved tab is read-only for typists — no Edit or
              // Delete. Reset-approval stays available for admins.
              onPdf={() => openFieldReportPreview(report.type, report.id)}
              onEmail={() => emailPDFFromBackend(report.type, report)}
              onReset={() => resetApproval(report.type, report.id)}
              isAdmin={isAdmin}
              PhotoReadinessDot={PhotoReadinessDot}
            />
          ))}
        </div>
      )}
    </>
  );
}
